import { Fragment } from 'react'
import HighlightedTextBodySpan, { HighlightingState } from './HighlightedTextBodySpan'
import type {
  KeyObjectType,
  KeyphraseCategory,
  KeywordCategory,
  KeywordsAndPhrasesReducerState,
  SingleKey,
} from 'reducers/KeywordsAndPhrasesReducer/KeywordsAndPhrasesReducer.d'

export const getKeywordsFromReducer = (
  reducerState: KeywordsAndPhrasesReducerState,
): { key: string; highlightingType: KeyObjectType }[] => {
  const allKeywords = { keywords: reducerState.keywords, keyphrases: reducerState.keyphrases }
  return Object.entries(allKeywords).flatMap(([typeKey, popoverTab]) => {
    // For each tab, get the key details object e.g. required, optional, avoid
    return Object.values(popoverTab).flatMap(keywordDetails => {
      const typesafeKeyDetailsObject = keywordDetails as KeywordCategory | KeyphraseCategory
      return Object.values(typesafeKeyDetailsObject).flatMap(singleKeyArray => {
        return (singleKeyArray as SingleKey[]).map(({ key }) => ({
          key,
          highlightingType: typeKey === 'keywords' ? 'keyword' : 'keyphrase',
        }))
      })
    })
  })
}

export const getKeywordMatchingRegexTest = (stringIn: string) => {
  // Allow for the following characters to be considered as word boundaries: whitespace, semicolon, comma, exclamation mark, hyphen
  const allowableCharacters = 'A-Za-z0-9.'
  // Match the keyword only if it is not preceded or followed by any of the allowable characters
  // Also match the keyword if it has a period before or after it which marks the beginning or end of a sentence
  return `(?:(?<![${allowableCharacters}]+)|(?<=^\\.+(?<=[^A-Za-z0-9])))${stringIn}(?:(?![${allowableCharacters}]+)|(?=\\.+(?:[^A-Za-z0-9]|$)))`
}

export function parseStringToJSX(
  inputString: string,
  keyphrases: { key: string; highlightingType: KeyObjectType }[],
  activeKeyword: string | null,
  nestedHighlightingStateOverride: 'active' | 'disabled' | 'inactive' | null = null,
  isNested: boolean = false,
): JSX.Element | string {
  // Remove empty keyphrases from the array
  const filteredKeyphrases = keyphrases.filter(keyphrase => keyphrase.key.trim() !== '')
  if (!filteredKeyphrases.length) return inputString

  // Sort the keyphrases by length in descending order
  const sortedKeyphrases = filteredKeyphrases.sort((a, b) => b.key.length - a.key.length)

  // Escape special characters in the keywords to avoid regex issues
  const escapedKeyphrases = sortedKeyphrases.map(keyword => {
    return getKeywordMatchingRegexTest(escapeRegExp(keyword.key))
  })

  // Create a regular expression pattern to match the keywords
  const keywordPattern = new RegExp(`(${escapedKeyphrases.join('|')})`, 'gi')

  // Split the input string into an array of substrings and matches
  const parts = inputString.split(keywordPattern)

  // Create an array of JSX elements
  const parsedElements = parts
    .map((part, index) => {
      if (keywordPattern.test(part)) {
        let highlightingState: HighlightingState | null = null
        if (nestedHighlightingStateOverride) {
          highlightingState =
            activeKeyword && activeKeyword.toLowerCase() === part.toLowerCase()
              ? 'active'
              : nestedHighlightingStateOverride
        } else {
          highlightingState =
            !activeKeyword || activeKeyword.toLowerCase() === part.toLowerCase() ? 'active' : 'disabled'
        }

        const keyphraseObject = keyphrases.find(({ key }) => {
          return key.toLowerCase() === part.toLowerCase()
        })
        const nestedKeyphrases = sortedKeyphrases.filter(
          ({ key }) => key.toLowerCase() !== part.toLowerCase() && part.toLowerCase().includes(key.toLowerCase()),
        )
        return (
          <HighlightedTextBodySpan
            key={`${part}_${index}`}
            highlightingType={keyphraseObject?.highlightingType || 'keyphrase'}
            highlightingState={highlightingState}
            isNested={isNested}
          >
            {/* if nested keyword/phrase, parse into another span but set inactive to remove styling */}
            {nestedKeyphrases.length > 0
              ? parseStringToJSX(part, nestedKeyphrases, activeKeyword, 'inactive', true) || <></>
              : part}
          </HighlightedTextBodySpan>
        )
      } else {
        return part.trim() ?? null
      }
    })
    .filter(Boolean)

  return <Fragment key={inputString}>{parsedElements}</Fragment>
}

// Helper function to escape special characters in a string for use in a regular expression
export function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
